/**
 * AppRoutes.tsx -- Route registrations for the dashboard sub-app.
 *
 * Declares all 93 routes from the canonical route table (specs/prd.md SS18.2).
 * Screen-component imports from ./screens/** are lazy-loaded via lazyScreen
 * (FR-001). Non-screen imports (hooks, providers, primitives, layout) stay eager
 * per the positive heuristic rule (specs/prd.md SS7A).
 *
 * The route table is the single source of truth for all URL-to-component mappings.
 * No wouter -- uses the lightweight hash router at lib/router/.
 */
import React from "react";
import { Route, Switch, useHashLocation } from "./lib/router";
import { lazyScreen } from "./lib/lazyScreen";

// ---------------------------------------------------------------------------
// Eager imports — NOT screen components (positive heuristic rule: stays eager)
// ---------------------------------------------------------------------------
import Today from "./screens/today/Today"; // shell entry, not a screen
import RoleGate from "./components/layout/RoleGate";
import { RefinementRun } from "./screens/workspace/RefinementRun"; // named export, not default
import SignIn from "./screens/auth/SignIn"; // auth perimeter, stays eager

// Brief Authoring imports (Section 11.3)
import type { Id } from "../../../convex/_generated/dataModel";
import { useFeatureFlag } from "./api/hooks/useFeatureFlag";
import { SkeletonRow, ErrorState, Soft404 } from "./components/primitives";
import ConvexErrorBoundary from "./components/cross-cutting/ConvexErrorBoundary";




// ---------------------------------------------------------------------------
// Lazy screen imports — default-exported components from ./screens/**
// ---------------------------------------------------------------------------

// Tier-A screens
const CommandCenter = lazyScreen(
  () => import("./screens/today/CommandCenter"),
  { skeleton: "dashboard" },
);
const RefinementsList = lazyScreen(
  () => import("./screens/workspace/RefinementsList"),
  { skeleton: "list" },
);
const BriefAnalyzer = lazyScreen(
  () => import("./screens/workspace/BriefAnalyzer"),
  { skeleton: "form" },
);
// BriefWorkshop — design-parity port of #initiate (two-column + live signals).
// Mounted by InitiateRoute / InitiateEditRoute when feature flag
// `brief_workshop_v1` is enabled; otherwise BriefAnalyzer (above) renders.
const BriefWorkshop = lazyScreen(
  () => import("./screens/workspace/BriefWorkshop"),
  { skeleton: "form" },
);

// Tier-B: workspace + initiative + Library screens (T312)
const WorkspaceList = lazyScreen(
  () => import("./screens/workspaces/WorkspaceList"),
  { skeleton: "list" },
);
const WorkspaceHome = lazyScreen(
  () => import("./screens/workspace/WorkspaceHome"),
  { skeleton: "detail" },
);
const WorkspaceConversations = lazyScreen(
  () => import("./screens/workspace/WorkspaceConversations"),
  { skeleton: "list" },
);
const WorkspaceRefinements = lazyScreen(
  () => import("./screens/workspace/WorkspaceRefinements"),
  { skeleton: "list" },
);
const InitiativeHome = lazyScreen(
  () => import("./screens/initiative/InitiativeHome"),
  { skeleton: "dashboard" },
);
const InitiativeSettings = lazyScreen(
  () => import("./screens/initiative/InitiativeSettings"),
  { skeleton: "form" },
);
const InitiativeWorkshop = lazyScreen(
  () => import("./screens/initiative/InitiativeWorkshop"),
  { skeleton: "detail" },
);
const LaunchRun = lazyScreen(
  () => import("./screens/initiative/LaunchRun"),
  { skeleton: "detail" },
);
const BriefsAssets = lazyScreen(
  () => import("./screens/initiative/BriefsAssets"),
  { skeleton: "list" },
);
const ImportPlan = lazyScreen(
  () => import("./screens/initiatives/ImportPlan"),
  { skeleton: "form" },
);
const Library = lazyScreen(
  () => import("./screens/library/Library"),
  { skeleton: "none" },
);

// Tier-B: run + observer screens (T316)
const RunDetail = lazyScreen(
  () => import("./screens/run/RunDetail"),
  { skeleton: "detail" },
);
const TraceView = lazyScreen(
  () => import("./screens/run/TraceView"),
  { skeleton: "trace" },
);
const VoiceSessionLive = lazyScreen(
  () => import("./screens/run/VoiceSessionLive"),
  { skeleton: "detail" },
);
const BrowserRun = lazyScreen(
  () => import("./screens/run/BrowserRun"),
  { skeleton: "detail" },
);
const EmailThread = lazyScreen(
  () => import("./screens/run/EmailThread"),
  { skeleton: "detail" },
);

// Tier-B: planSurface + taskSurface screens (T314)
const PlanView = lazyScreen(
  () => import("./screens/plan/PlanView"),
  { skeleton: "detail" },
);
const PlanCockpit = lazyScreen(
  () => import("./screens/plan/PlanCockpit"),
  { skeleton: "dashboard" },
);
const PhaseGroups = lazyScreen(
  () => import("./screens/plan/PhaseGroups"),
  { skeleton: "list" },
);
const TaskList = lazyScreen(
  () => import("./screens/task/TaskList"),
  { skeleton: "list" },
);
const TaskDetail = lazyScreen(
  () => import("./screens/task/TaskDetail"),
  { skeleton: "detail" },
);

// Phase 3 Tier-C: insights screens
const ActivityOverview = lazyScreen(
  () => import("./screens/insights/ActivityOverview"),
  { skeleton: "list" },
);
const SpendingOverview = lazyScreen(
  () => import("./screens/insights/SpendingOverview"),
  { skeleton: "dashboard" },
);
const SpendingDrilldown = lazyScreen(
  () => import("./screens/insights/SpendingDrilldown"),
  { skeleton: "detail" },
);
const QualityAlerts = lazyScreen(
  () => import("./screens/insights/QualityAlerts"),
  { skeleton: "list" },
);
const CIVisualisation = lazyScreen(
  () => import("./screens/insights/CIVisualisation"),
  { skeleton: "visualisation" },
);
const FrictionAnalytics = lazyScreen(
  () => import("./screens/insights/FrictionAnalytics"),
  { skeleton: "visualisation" },
);
const AssignmentConsole = lazyScreen(
  () => import("./screens/insights/AssignmentConsole"),
  { skeleton: "detail" },
);
const TeamCapacity = lazyScreen(
  () => import("./screens/insights/TeamCapacity"),
  { skeleton: "visualisation" },
);

// Phase 4 Tier-D: access keys screens
const AccessKeysScreen = lazyScreen(
  () => import("./screens/keys/AccessKeys"),
  { skeleton: "list" },
);
const AccessKeysAdminScreen = lazyScreen(
  () => import("./screens/admin/AccessKeysAdmin"),
  { skeleton: "list" },
);

// Phase F-5: graduated deferred routes (v.1-FE FR-012)
const CalendarConnect = lazyScreen(
  () => import("./screens/perimeter/CalendarConnect"),
  { skeleton: "detail" },
);
const CustomIntegration = lazyScreen(
  () => import("./screens/perimeter/CustomIntegration"),
  { skeleton: "detail" },
);
const PatternShowcase = lazyScreen(
  () => import("./screens/library/PatternShowcase"),
  { skeleton: "none" },
);
const RealtimeShowcase = lazyScreen(
  () => import("./screens/library/RealtimeShowcase"),
  { skeleton: "none" },
);

// Phase 4 Tier-D: pipeline screens
const PipelineDashboard = lazyScreen(
  () => import("./screens/pipeline/PipelineDashboard"),
  { skeleton: "dashboard" },
);
const DeliveryQueue = lazyScreen(
  () => import("./screens/pipeline/DeliveryQueue"),
  { skeleton: "list" },
);
const ChangeProposals = lazyScreen(
  () => import("./screens/pipeline/ChangeProposals"),
  { skeleton: "list" },
);
const ProposalDetail = lazyScreen(
  () => import("./screens/pipeline/ProposalDetail"),
  { skeleton: "detail" },
);
const ReviewCenter = lazyScreen(
  () => import("./screens/pipeline/ReviewCenter"),
  { skeleton: "list" },
);
const BranchHealth = lazyScreen(
  () => import("./screens/pipeline/BranchHealth"),
  { skeleton: "dashboard" },
);
const PipelineActivity = lazyScreen(
  () => import("./screens/pipeline/PipelineActivity"),
  { skeleton: "list" },
);
const SystemHealth = lazyScreen(
  () => import("./screens/pipeline/SystemHealth"),
  { skeleton: "dashboard" },
);
const SecurityIntegrity = lazyScreen(
  () => import("./screens/pipeline/SecurityIntegrity"),
  { skeleton: "dashboard" },
);
const Deployments = lazyScreen(
  () => import("./screens/pipeline/Deployments"),
  { skeleton: "list" },
);
const Releases = lazyScreen(
  () => import("./screens/pipeline/Releases"),
  { skeleton: "list" },
);
const ReleaseDetail = lazyScreen(
  () => import("./screens/pipeline/ReleaseDetail"),
  { skeleton: "detail" },
);
const PipelineSettings = lazyScreen(
  () => import("./screens/pipeline/PipelineSettings"),
  { skeleton: "form" },
);
const PipelineNotifications = lazyScreen(
  () => import("./screens/pipeline/PipelineNotifications"),
  { skeleton: "form" },
);
const PipelineOnboarding = lazyScreen(
  () => import("./screens/pipeline/PipelineOnboarding"),
  { skeleton: "form" },
);
const EmergencyOps = lazyScreen(
  () => import("./screens/pipeline/EmergencyOps"),
  { skeleton: "dashboard" },
);
const Compare = lazyScreen(
  () => import("./screens/pipeline/Compare"),
  { skeleton: "visualisation" },
);
const BranchCoordination = lazyScreen(
  () => import("./screens/pipeline/BranchCoordination"),
  { skeleton: "dashboard" },
);

// Phase 3 Tier-C: playbooks screens
const PlaybookLeaderboard = lazyScreen(
  () => import("./screens/playbooks/PlaybookLeaderboard"),
  { skeleton: "list" },
);
const PlaybookEditor = lazyScreen(
  () => import("./screens/playbooks/PlaybookEditor"),
  { skeleton: "form" },
);
const PlaybookTests = lazyScreen(
  () => import("./screens/playbooks/PlaybookTests"),
  { skeleton: "list" },
);
const PlaybookTestDetail = lazyScreen(
  () => import("./screens/playbooks/PlaybookTestDetail"),
  { skeleton: "detail" },
);
const CalibrationRitual = lazyScreen(
  () => import("./screens/playbooks/CalibrationRitual"),
  { skeleton: "dashboard" },
);
const CalibrationAdmin = lazyScreen(
  () => import("./screens/playbooks/CalibrationAdmin"),
  { skeleton: "list" },
);

// Phase 4 Tier-D: admin screens
const ActivityLog = lazyScreen(
  () => import("./screens/admin/ActivityLog"),
  { skeleton: "list" },
);
const SensitiveLog = lazyScreen(
  () => import("./screens/admin/SensitiveLog"),
  { skeleton: "list" },
);
const AdminUsers = lazyScreen(
  () => import("./screens/admin/Users"),
  { skeleton: "list" },
);
const CloudServers = lazyScreen(
  () => import("./screens/admin/CloudServers"),
  { skeleton: "list" },
);
const AdminWebhooks = lazyScreen(
  () => import("./screens/admin/Webhooks"),
  { skeleton: "list" },
);
const AdminDeliveries = lazyScreen(
  () => import("./screens/admin/Deliveries"),
  { skeleton: "list" },
);
const PlanUsage = lazyScreen(
  () => import("./screens/admin/PlanUsage"),
  { skeleton: "dashboard" },
);
const PlansCompare = lazyScreen(
  () => import("./screens/admin/PlansCompare"),
  { skeleton: "list" },
);
const DeleteMyData = lazyScreen(
  () => import("./screens/admin/DeleteMyData"),
  { skeleton: "form" },
);
const Archive = lazyScreen(
  () => import("./screens/admin/Archive"),
  { skeleton: "list" },
);
const AdminVersion = lazyScreen(
  () => import("./screens/admin/Version"),
  { skeleton: "detail" },
);
const AdminActions = lazyScreen(
  () => import("./screens/admin/Actions"),
  { skeleton: "list" },
);
const AdminJobs = lazyScreen(
  () => import("./screens/admin/Jobs"),
  { skeleton: "list" },
);

// Standalone: notifications
const NotificationsInboxPage = lazyScreen(
  () => import("./screens/notifications/NotificationsInboxPage"),
  { skeleton: "list" },
);

// Phase 5 Tier-D: perimeter screens
const Integrations = lazyScreen(
  () => import("./screens/perimeter/Integrations"),
  { skeleton: "list" },
);
const SsoSetup = lazyScreen(
  () => import("./screens/perimeter/SsoSetup"),
  { skeleton: "form" },
);
const IntegrationDetail = lazyScreen(
  () => import("./screens/perimeter/IntegrationDetail"),
  { skeleton: "detail" },
);
const StatusPage = lazyScreen(
  () => import("./screens/perimeter/StatusPage"),
  { skeleton: "dashboard" },
);
const Profile = lazyScreen(
  () => import("./screens/perimeter/Profile"),
  { skeleton: "form" },
);
const UserSettings = lazyScreen(
  () => import("./screens/perimeter/UserSettings"),
  { skeleton: "form" },
);
const Onboarding = lazyScreen(
  () => import("./screens/auth/Onboarding"),
  { skeleton: "form" },
);

// Phase 6: static + auth perimeter screens (T328)
const AuthCallback = lazyScreen(
  () => import("./screens/auth/AuthCallback"),
  { skeleton: "none" },
);
const Welcome = lazyScreen(
  () => import("./screens/auth/Welcome"),
  { skeleton: "none" },
);
const Reference = lazyScreen(
  () => import("./screens/docs/Reference"),
  { skeleton: "none" },
);
const Guide = lazyScreen(
  () => import("./screens/docs/Guide"),
  { skeleton: "none" },
);
const PublicLanding = lazyScreen(
  () => import("./screens/docs/PublicLanding"),
  { skeleton: "none" },
);

// ---------------------------------------------------------------------------
// Fix D (T315): /showcase/* gate.
//
// Reads the `dev_showcase` feature flag (registered in T001, off in prod) and
// renders <Soft404 /> when the flag is not explicitly true. The factory shape
// lets the existing Route registration stay declarative (one `component={...}`
// per route). The flag is CONSUMED here; it is NOT re-registered. While the
// query is loading (`undefined`), treat as off — dev showcase routes are not
// time-sensitive, so a momentary 404 on first paint is preferable to flashing
// real showcase content before resolution.
// ---------------------------------------------------------------------------
function ShowcaseGate(Inner: React.ComponentType): React.ComponentType {
  return function GatedShowcase() {
    const enabled = useFeatureFlag("dev_showcase");
    if (enabled !== true) return <Soft404 />;
    return <Inner />;
  };
}

// ---------------------------------------------------------------------------
// Soft-empty-state fallback for the #/command route when the user's role
// does not meet the "Operator" threshold (FR-006 / PRD SS5.5).
// ---------------------------------------------------------------------------
function RoleGateFallback() {
  return (
    <div style={{ textAlign: "center", padding: "4rem 1rem" }}>
      <p>You do not have access to the Command Center.</p>
      <a href="#/today">Go to your home &rarr;</a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Named React component for the create-mode route.
// ---------------------------------------------------------------------------
export function InitiateRoute() {
  const enabled = useFeatureFlag("brief_analyzer_v1");
  // Independent toggle for the design-parity workshop. When on, mount
  // BriefWorkshop in place of the legacy BriefAnalyzer form. Reads `undefined`
  // until Convex resolves; treat that as off (don't block render).
  const workshop = useFeatureFlag("brief_workshop_v1") === true;
  if (enabled === undefined) return <SkeletonRow count={5} />;
  if (!enabled) return <Soft404 />;
  return (
    <ConvexErrorBoundary fallback={<ErrorState onRetry={() => window.location.reload()} />}>
      {workshop
        ? <BriefWorkshop briefId={undefined} />
        : <BriefAnalyzer briefId={undefined} />}
    </ConvexErrorBoundary>
  );
}

// ---------------------------------------------------------------------------
// Named React component for the edit-mode route.
// ---------------------------------------------------------------------------
export function InitiateEditRoute({ briefId }: { briefId?: string }) {
  const enabled = useFeatureFlag("brief_analyzer_v1");
  const workshop = useFeatureFlag("brief_workshop_v1") === true;
  if (enabled === undefined) return <SkeletonRow count={5} />;
  if (!enabled) return <Soft404 />;
  return (
    <ConvexErrorBoundary fallback={<ErrorState onRetry={() => window.location.reload()} />}>
      {workshop
        ? <BriefWorkshop briefId={briefId as Id<"briefAnalyses">} /> /* eslint-disable-line @typescript-eslint/consistent-type-assertions -- same bounded cast pattern as the legacy branch below */
        : <BriefAnalyzer briefId={briefId as Id<"briefAnalyses">} /> /* eslint-disable-line @typescript-eslint/consistent-type-assertions -- bounded unsafe cast; see InitiateEditRoute comment in prior version */}
    </ConvexErrorBoundary>
  );
}

/**
 * Dashboard route table -- all 93 routes.
 *
 * Routes 1-6:   Tier-A (real screen components)
 * Routes 7-83:  Tier-B/C/D (ComingSoon placeholders)
 * Routes 84-93: Static / auth perimeter
 *
 * Route numbering matches specs/prd.md SS18.2.
 */
export function AppRoutes(): JSX.Element {
  const [path] = useHashLocation();

  return (
    <div className="motion__page-cross-fade" key={path}>
    <Switch>
      {/* ── Tier-A: 6 live screens ─────────────────────────────────── */}

      {/* 1-2. workspaceHome.activity-overview (Today dispatcher) */}
      <Route path="/" component={Today} />
      <Route path="/today" component={Today} />

      {/* 3. runWorkflow.history (Command Center, RoleGate min="Operator") */}
      <Route path="/command">
        {() => (
          <RoleGate min="Operator" fallback={<RoleGateFallback />}>
            <CommandCenter />
          </RoleGate>
        )}
      </Route>

      {/* 4. runWorkflow.analyze (BriefAnalyzer, create + edit modes) */}
      <Route path="/analyze/:id">
        {({ id }) => <InitiateEditRoute briefId={id} />}
      </Route>
      <Route path="/initiate" component={InitiateRoute} />
      <Route path="/initiate/:briefId">
        {({ briefId }) => <InitiateEditRoute briefId={briefId!} />}
      </Route>

      {/* 5. runWorkflow.refinement-detail */}
      <Route path="/refine/:id" component={RefinementRun} />

      {/* 6. runWorkflow.refinement-list + refinement-list-by-workspace */}
      <Route path="/refine" component={RefinementsList} />
      <Route path="/workspace/:slug/refinements" component={RefinementsList} />

      {/* ── Tier-B: workspaceHome modes (T312) ─────────────────────── */}
      <Route path="/workspaces" component={WorkspaceList} />
      <Route path="/workspaces/:id/conversations">
        {(params: { id?: string }) => <WorkspaceConversations workspaceId={params.id!} />}
      </Route>
      <Route path="/workspaces/:id/refinements">
        {(params: { id?: string }) => <WorkspaceRefinements workspaceId={params.id!} />}
      </Route>
      <Route path="/workspaces/:id">
        {(params: { id?: string }) => <WorkspaceHome workspaceId={params.id!} />}
      </Route>

      {/* ── Tier-B: initiativeWorkflow modes (T312) ────────────────── */}
      <Route path="/initiatives/import" component={ImportPlan} />
      <Route path="/initiatives/:id/settings">
        {(params: { id?: string }) => <InitiativeSettings initiativeId={params.id!} />}
      </Route>
      <Route path="/initiatives/:id/workshop">
        {(params: { id?: string }) => <InitiativeWorkshop initiativeId={params.id!} />}
      </Route>
      <Route path="/initiatives/:id/launch">
        {(params: { id?: string }) => <LaunchRun initiativeId={params.id!} />}
      </Route>
      <Route path="/initiatives/:id/briefs">
        {(params: { id?: string }) => <BriefsAssets initiativeId={params.id!} />}
      </Route>
      <Route path="/initiatives/:id">
        {(params: { id?: string }) => <InitiativeHome initiativeId={params.id!} />}
      </Route>

      {/* ── Tier-B: planSurface modes ──────────────────────────────── */}
      <Route path="/plans/:id/cockpit">
        {(params: { id?: string }) => <PlanCockpit planId={params.id ?? ""} />}
      </Route>
      <Route path="/plans/:id/phases">
        {(params: { id?: string }) => <PhaseGroups planId={params.id ?? ""} />}
      </Route>
      <Route path="/plans/:id">
        {(params: { id?: string }) => <PlanView planId={params.id ?? ""} />}
      </Route>

      {/* ── Tier-B: taskSurface modes ──────────────────────────────── */}
      <Route path="/tasks/:id">
        {(params: { id?: string }) => <TaskDetail taskId={params.id ?? ""} />}
      </Route>
      <Route path="/tasks" component={TaskList} />

      {/* ── Tier-B: runWorkflow modes ──────────────────────────────── */}
      <Route path="/runs/:id/trace">
        {(params: { id?: string }) => <TraceView runId={params.id!} />}
      </Route>
      <Route path="/runs/:id">
        {(params: { id?: string }) => <RunDetail runId={params.id!} />}
      </Route>

      {/* ── Tier-B: runObserver modes ──────────────────────────────── */}
      <Route path="/observers/voice/:id">
        {(params: { id?: string }) => <VoiceSessionLive sessionId={params.id!} />}
      </Route>
      <Route path="/voice/:id">
        {(params: { id?: string }) => <VoiceSessionLive sessionId={params.id!} />}
      </Route>
      <Route path="/observers/browser/:id">
        {(params: { id?: string }) => <BrowserRun sessionId={params.id!} />}
      </Route>
      <Route path="/observers/email/:id">
        {(params: { id?: string }) => <EmailThread sessionId={params.id!} />}
      </Route>

      {/* ── Tier-C: insights modes ─────────────────────────────────── */}
      {/* TopNav "Activity" links to /activity; map it to the activity overview. */}
      <Route path="/activity" component={ActivityOverview} />
      <Route path="/insights" component={ActivityOverview} />
      <Route path="/insights/spending" component={SpendingOverview} />
      <Route path="/insights/spending/:id" component={SpendingDrilldown} />
      <Route path="/insights/quality" component={QualityAlerts} />
      <Route path="/insights/ci" component={CIVisualisation} />
      <Route path="/insights/friction" component={FrictionAnalytics} />
      <Route path="/insights/assignment" component={AssignmentConsole} />
      <Route path="/insights/team-capacity" component={TeamCapacity} />

      {/* ── Tier-C: playbooks modes ────────────────────────────────── */}
      <Route path="/playbooks/calibration/admin" component={CalibrationAdmin} />
      <Route path="/playbooks/calibration" component={CalibrationRitual} />
      <Route path="/playbooks/tests/:id" component={PlaybookTestDetail} />
      <Route path="/playbooks/tests" component={PlaybookTests} />
      <Route path="/playbooks/editor" component={PlaybookEditor} />
      <Route path="/playbooks" component={PlaybookLeaderboard} />

      {/* ── Tier-D: pipeline modes ─────────────────────────────────── */}
      <Route path="/pipeline" component={PipelineDashboard} />
      <Route path="/pipeline/queue" component={DeliveryQueue} />
      <Route path="/pipeline/proposals" component={ChangeProposals} />
      <Route path="/pipeline/proposals/:id">
        {(params: { id?: string }) => <ProposalDetail proposalId={params.id} />}
      </Route>
      <Route path="/pipeline/review" component={ReviewCenter} />
      <Route path="/pipeline/branches" component={BranchHealth} />
      <Route path="/pipeline/activity" component={PipelineActivity} />
      <Route path="/pipeline/system" component={SystemHealth} />
      <Route path="/pipeline/security" component={SecurityIntegrity} />
      <Route path="/pipeline/deployments" component={Deployments} />
      <Route path="/pipeline/releases" component={Releases} />
      <Route path="/pipeline/releases/:id">
        {(params: { id?: string }) => <ReleaseDetail releaseId={params.id} />}
      </Route>
      <Route path="/pipeline/settings" component={PipelineSettings} />
      <Route path="/pipeline/notifications" component={PipelineNotifications} />
      <Route path="/pipeline/onboarding" component={PipelineOnboarding} />
      <Route path="/pipeline/emergency" component={EmergencyOps} />
      <Route path="/pipeline/compare" component={Compare} />
      <Route path="/pipeline/coordination" component={BranchCoordination} />

      {/* ── Tier-D: admin modes ────────────────────────────────────── */}
      <Route path="/admin/activity" component={ActivityLog} />
      <Route path="/admin/sensitive" component={SensitiveLog} />
      <Route path="/admin/users" component={AdminUsers} />
      <Route path="/admin/servers" component={CloudServers} />
      <Route path="/admin/webhooks" component={AdminWebhooks} />
      <Route path="/admin/deliveries" component={AdminDeliveries} />
      <Route path="/admin/plan-usage" component={PlanUsage} />
      <Route path="/admin/plans" component={PlansCompare} />
      <Route path="/admin/delete-my-data" component={DeleteMyData} />
      <Route path="/admin/archive" component={Archive} />
      <Route path="/admin/version" component={AdminVersion} />
      <Route path="/admin/actions" component={AdminActions} />
      <Route path="/admin/jobs" component={AdminJobs} />

      {/* ── Tier-D: accessKeys modes ───────────────────────────────── */}
      <Route path="/access-keys" component={AccessKeysScreen} />
      <Route path="/admin/access-keys" component={AccessKeysAdminScreen} />

      {/* ── Tier-D: perimeter modes ────────────────────────────────── */}
      <Route path="/integrations" component={Integrations} />
      <Route path="/integrations/sso" component={SsoSetup} />
      <Route path="/integrations/calendar" component={CalendarConnect} />
      <Route path="/integrations/custom" component={CustomIntegration} />
      <Route path="/integrations/:id">
        {(params: { id?: string }) => <IntegrationDetail id={params.id} />}
      </Route>
      <Route path="/status" component={StatusPage} />
      <Route path="/onboarding" component={Onboarding} />
      <Route path="/profile" component={Profile} />
      <Route path="/settings" component={UserSettings} />

      {/* ── Standalone: notificationsInbox ─────────────────────────── */}
      <Route path="/notifications" component={NotificationsInboxPage} />

      {/* ── Static / Auth Perimeter (T328) ────────────────────────── */}
      <Route path="/signin" component={SignIn} />
      <Route path="/auth/callback" component={AuthCallback} />
      <Route path="/welcome" component={Welcome} />
      <Route path="/reference" component={Reference} />
      <Route path="/guide" component={Guide} />
      <Route path="/library" component={Library} />
      {/* Fix D (T315): /showcase/* gated behind `dev_showcase` flag
          (registered in T001; off in prod). When the flag is off the
          showcase routes render <Soft404 /> so they do not contribute
          to the render-rate denominator. The flag is consumed here —
          NOT re-registered. */}
      <Route path="/showcase/patterns" component={ShowcaseGate(PatternShowcase)} />
      <Route path="/showcase/realtime" component={ShowcaseGate(RealtimeShowcase)} />
      <Route path="/public" component={PublicLanding} />

      {/* ── Catch-all 404 ──────────────────────────────────────────── */}
      <Route path="*">
        {() => <Soft404 />}
      </Route>
    </Switch>
    </div>
  );
}

export default AppRoutes;
